<script lang="ts" setup>
import type {
  Configuration,
  SurfaceTypesMapValue,
  SurfaceOptionsGroup,
  SurfaceMaps,
} from '~/types/configurator'

const activePanel = defineModel<string | null>()

const props = defineProps<{
  configuration: Configuration | null
  currentValues: Configuration['values']
  surfaceCategoryMismatch: Map<string, Map<number, boolean>>
  globalSurfaceChanges: Map<string, Map<number, boolean>>
  surfaceOptionsGroups: Map<string, SurfaceOptionsGroup>
  surfaceTypes: Map<number, SurfaceTypesMapValue>
  surfaceMaps: SurfaceMaps
  surfaceOptionsGroupsPrices: Map<string, Map<number, number>>
}>()

const emit = defineEmits<{
  setChosenParts: [
    surfaceOptionsGroupsKey: string,
    surfaceOptionGroupsKey: number,
    partCategoryNid?: number,
    accessoryNid?: number | null,
  ]
  setGlobalSurfaceChanges: [
    surfaceOptionsGroupsKey: string,
    surfaceOptionGroupsKey: number,
    globalSurfaceChange: boolean,
    value?: Configuration['values'][number],
  ]
}>()

const userStore = useUser()
const { showPrices } = storeToRefs(userStore)
</script>

<template>
  <template
    v-for="(
      [groupKey, surfaceOptionsGroup], index
    ) in surfaceOptionsGroups.entries()"
    :key="index"
  >
    <div
      v-if="surfaceOptionsGroup.surfaceOptionGroups.size > 0"
      class="grid grid-cols-[4rem_1fr] border-b-4 border-neutral-200 last:relative last:border-b-0 last:after:absolute last:after:bottom-[-0.25rem] last:after:h-1 last:after:w-full last:after:bg-neutral-200 last:after:content-[''] 2xl:grid-cols-[5rem_1fr]"
      :class="{
        '!grid-cols-[4rem] gap-1 bg-neutral-200 2xl:!grid-cols-[5rem]':
          activePanel,
      }"
    >
      <template
        v-for="[key, surfaceOptionGroup] in activePanel
          ? [...surfaceOptionsGroup.surfaceOptionGroups.entries()]
          : [...surfaceOptionsGroup.surfaceOptionGroups.entries()].slice(0, 1)"
        :key="key"
      >
        <UiTooltip
          v-if="surfaceOptionGroup.surfaceOptions.length"
          placement="right"
          :label="
            surfaceOptionGroup.surfaceType?.title ||
            surfaceOptionGroup.surfaceOptions.at(0)?.assignFabricTitle ||
            ''
          "
          :disabled="!activePanel"
          :offset-size="1"
          class="font-pragmatica-ext rounded-none border-[1px] border-neutral-200 px-2 py-2 text-sm font-bold drop-shadow-sm 2xl:text-base"
        >
          <div
            class="rows-span-full flex h-full w-[4rem] items-center justify-center bg-white 2xl:w-[5rem]"
            :class="{
              'aspect-square h-[4rem] cursor-pointer hover:!bg-neutral-100 2xl:h-[5rem]':
                activePanel,
              '!bg-neutral-200':
                activePanel === SURFACES_OPTIONS_PANEL + groupKey + key,
            }"
            @click="
              () => {
                if (activePanel) {
                  if (activePanel === SURFACES_OPTIONS_PANEL + groupKey + key) {
                    activePanel = null
                  } else {
                    if (globalSurfaceChanges.get(groupKey)?.get(key)) {
                      emit('setChosenParts', groupKey, key)
                    } else {
                      emit(
                        'setChosenParts',
                        groupKey,
                        key,
                        surfaceOptionGroup.surfaceOptions.at(0)
                          ?.partCategoryNid,
                        surfaceOptionGroup.surfaceOptions.at(0)?.accessoryNid
                      )
                    }
                    activePanel = SURFACES_OPTIONS_PANEL + groupKey + key
                  }
                }
              }
            "
          >
            <NuxtImg
              v-if="surfaceOptionsGroup?.icon?.url"
              :src="withHost(surfaceOptionsGroup?.icon?.url ?? '')"
              class="h-5 w-5"
              :class="{
                'opacity-[.5]':
                  activePanel !== SURFACES_OPTIONS_PANEL + groupKey + key &&
                  activePanel !== null,
              }"
              alt=""
            />
            <Icon
              v-else-if="groupKey.startsWith('no-icon-')"
              name="fa6-solid:gear"
              class="h-5 w-5"
              :class="{
                'text-neutral-400':
                  activePanel !== SURFACES_OPTIONS_PANEL + groupKey + key &&
                  activePanel !== null,
              }"
            />
          </div>
        </UiTooltip>
      </template>
      <div v-if="!activePanel">
        <template
          v-for="[key, surfaceOptionGroup] in [
            ...surfaceOptionsGroup.surfaceOptionGroups.entries(),
          ]"
          :key="key"
        >
          <template v-if="surfaceOptionGroup.surfaceOptions.length">
            <div
              v-if="surfaceOptionGroup.surfaceOptions.length !== 1"
              class="grid min-h-16 w-full grid-cols-3 items-center py-2 pl-2 pr-5 2xl:min-h-20 2xl:pl-5"
              :class="{
                'cursor-pointer hover:bg-neutral-100':
                  props.globalSurfaceChanges.get(groupKey)?.get(key),
                '!bg-red-50': props.surfaceCategoryMismatch
                  .get(groupKey)
                  ?.get(key),
                'hover:!bg-red-100':
                  props.surfaceCategoryMismatch.get(groupKey)?.get(key) &&
                  props.globalSurfaceChanges.get(groupKey)?.get(key),
              }"
              @click.stop="
                () => {
                  if (props.globalSurfaceChanges.get(groupKey)?.get(key)) {
                    emit('setChosenParts', groupKey, key)
                    activePanel = SURFACES_OPTIONS_PANEL + groupKey + key
                  }
                }
              "
            >
              <div>
                <p class="font-pragmatica-ext text-lg font-bold">
                  {{ surfaceOptionGroup.surfaceType?.title }}
                </p>
                <button
                  v-if="globalSurfaceChanges.get(groupKey)?.get(key)"
                  class="flex items-center text-left text-xs underline"
                  @click.stop="
                    emit('setGlobalSurfaceChanges', groupKey, key, false)
                  "
                >
                  <span>{{ $t('configurator.applyMultipleSurface') }}</span>
                  <Icon name="fa6-solid:arrow-down" class="ml-2" />
                </button>
              </div>
              <div>
                <p
                  v-if="
                    showPrices &&
                    !props.surfaceCategoryMismatch.get(groupKey)?.get(key)
                  "
                  class="font-pragmatica-ext justify-self-center text-center text-sm font-bold 2xl:text-lg"
                >
                  {{
                    configuratorUtils.getFormattedPrice(
                      props.surfaceOptionsGroupsPrices.get(groupKey)?.get(key),
                      props.configuration?.currency
                    )
                  }}
                </p>
              </div>
              <div class="justify-self-end">
                <button
                  v-show="globalSurfaceChanges.get(groupKey)?.get(key)"
                  class="flex items-center gap-4"
                >
                  <p class="text-right">
                    <span>
                      {{
                        configuratorUtils.findValue(
                          props.currentValues,
                          surfaceOptionGroup.surfaceOptions.at(0)
                            ?.partCategoryNid || 0,
                          surfaceOptionGroup.surfaceOptions.at(0)?.accessoryNid
                        )?.surfaceTitle
                      }}
                    </span>
                    <br />
                    <span
                      v-if="
                        configuratorUtils.findValue(
                          props.currentValues,
                          surfaceOptionGroup.surfaceOptions.at(0)
                            ?.partCategoryNid || 0,
                          surfaceOptionGroup.surfaceOptions.at(0)?.accessoryNid
                        )?.surfaceNid
                      "
                      class="text-sm font-bold"
                    >
                      {{ $t('configurator.category') }}
                      {{
                        configuratorUtils.findSurfaceByNid(
                          props.surfaceTypes,
                          props.surfaceMaps,
                          configuratorUtils.findValue(
                            props.currentValues,
                            surfaceOptionGroup.surfaceOptions.at(0)
                              ?.partCategoryNid || 0,
                            surfaceOptionGroup.surfaceOptions.at(0)
                              ?.accessoryNid
                          )?.surfaceNid
                        )?.category?.title
                      }}
                    </span>
                  </p>
                  <NuxtImg
                    v-if="
                      configuratorUtils.findValue(
                        props.currentValues,
                        surfaceOptionGroup.surfaceOptions.at(0)
                          ?.partCategoryNid || 0,
                        surfaceOptionGroup.surfaceOptions.at(0)?.accessoryNid
                      )?.surfaceImageSrc
                    "
                    :src="
                      withHost(
                        configuratorUtils.findValue(
                          props.currentValues,
                          surfaceOptionGroup.surfaceOptions.at(0)
                            ?.partCategoryNid || 0,
                          surfaceOptionGroup.surfaceOptions.at(0)?.accessoryNid
                        )?.surfaceImageSrc + IMG_SIZE_100_100_FIT
                      )
                    "
                    class="aspect-square h-12 rounded-full 2xl:h-16"
                    alt=""
                  />
                  <div
                    v-else
                    class="font-pragmatica-ext grid h-12 w-12 place-content-center rounded-full bg-black text-xs font-bold text-white 2xl:h-16 2xl:w-16 2xl:text-sm"
                  >
                    <span>
                      {{ props.configuration?.surfacesOptions[0]?.title }}
                    </span>
                  </div>
                </button>
              </div>
            </div>
            <div
              v-for="option in surfaceOptionGroup.surfaceOptions"
              v-show="
                !globalSurfaceChanges.get(groupKey)?.get(key) ||
                surfaceOptionsGroups.get(groupKey)?.surfaceOptionGroups.get(key)
                  ?.surfaceOptions.length === 1
              "
              :key="option.partCategoryNid"
              class="grid min-h-16 w-full cursor-pointer grid-cols-3 items-center p-2 py-2 pl-2 pr-5 hover:bg-neutral-100 2xl:min-h-20 2xl:pl-5"
              :class="{
                '!bg-red-50 hover:!bg-red-100': props.surfaceCategoryMismatch
                  .get(groupKey)
                  ?.get(key),
              }"
              @click.stop="
                () => {
                  emit(
                    'setChosenParts',
                    groupKey,
                    key,
                    option.partCategoryNid,
                    option.accessoryNid
                  )
                  activePanel = SURFACES_OPTIONS_PANEL + groupKey + key
                }
              "
            >
              <div>
                <p class="font-pragmatica-ext text-lg font-bold">
                  {{ option.assignFabricTitle }}
                </p>
                <button
                  v-if="
                    !globalSurfaceChanges.get(groupKey)?.get(key) &&
                    surfaceOptionsGroups
                      .get(groupKey)
                      ?.surfaceOptionGroups.get(key)?.surfaceOptions.length !==
                      1
                  "
                  class="flex items-center text-left text-xs underline"
                  @click.stop="
                    emit(
                      'setGlobalSurfaceChanges',
                      groupKey,
                      key,
                      true,
                      configuratorUtils.findValue(
                        props.currentValues,
                        option.partCategoryNid,
                        option.accessoryNid
                      )
                    )
                  "
                >
                  {{ $t('configurator.applySurfaceGlobally') }}
                </button>
              </div>
              <div>
                <p
                  v-if="
                    showPrices &&
                    surfaceOptionsGroups
                      .get(groupKey)
                      ?.surfaceOptionGroups.get(key)?.surfaceOptions.length ===
                      1 &&
                    !props.surfaceCategoryMismatch.get(groupKey)?.get(key)
                  "
                  class="font-pragmatica-ext justify-self-center text-center text-sm font-bold 2xl:text-lg"
                >
                  {{
                    configuratorUtils.getFormattedPrice(
                      props.surfaceOptionsGroupsPrices.get(groupKey)?.get(key),
                      props.configuration?.currency
                    )
                  }}
                </p>
              </div>
              <div
                class="flex items-center justify-center gap-4 justify-self-end"
              >
                <p class="text-right">
                  <span>
                    {{
                      configuratorUtils.findValue(
                        props.currentValues,
                        option.partCategoryNid,
                        option.accessoryNid
                      )?.surfaceTitle
                    }}
                  </span>
                  <br />
                  <span
                    v-if="
                      configuratorUtils.findValue(
                        props.currentValues,
                        option.partCategoryNid,
                        option.accessoryNid
                      )
                    "
                    class="text-sm font-bold"
                  >
                    {{ $t('configurator.category') }}
                    {{
                      configuratorUtils.findSurfaceByNid(
                        props.surfaceTypes,
                        props.surfaceMaps,
                        configuratorUtils.findValue(
                          props.currentValues,
                          option.partCategoryNid,
                          option.accessoryNid
                        )?.surfaceNid
                      )?.category?.title
                    }}
                  </span>
                </p>
                <NuxtImg
                  v-if="
                    configuratorUtils.findValue(
                      props.currentValues,
                      option.partCategoryNid,
                      option.accessoryNid
                    )?.surfaceImageSrc
                  "
                  :src="
                    withHost(
                      configuratorUtils.findValue(
                        props.currentValues,
                        option.partCategoryNid,
                        option.accessoryNid
                      )?.surfaceImageSrc + IMG_SIZE_100_100_FIT
                    )
                  "
                  class="aspect-square h-12 rounded-full 2xl:h-16"
                  alt=""
                />
                <div
                  v-else
                  class="font-pragmatica-ext grid aspect-square h-12 place-content-center rounded-full bg-black text-xs font-bold text-white 2xl:h-16 2xl:text-sm"
                >
                  <span>
                    {{ option.title }}
                  </span>
                </div>
              </div>
            </div>
            <div
              v-if="props.surfaceCategoryMismatch.get(groupKey)?.get(key)"
              class="flex w-full flex-row items-start justify-end gap-4 bg-red-50 py-2 pl-2 pr-5 2xl:pl-5"
            >
              <p class="self-center text-right text-xs text-red-800">
                {{ $t('configurator.categoryMismatch') }}
              </p>
              <span
                class="flex aspect-square min-w-10 items-center justify-center rounded-full bg-red-500 text-white"
              >
                <Icon
                  class="size-5 pb-0.5"
                  name="fa6-solid:triangle-exclamation"
                />
              </span>
            </div>
          </template>
        </template>
      </div>
    </div>
  </template>
</template>
